import React, { useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Pressable, StyleSheet, Platform, Animated, Easing, TextInput, Image, ScrollView } from 'react-native';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from './Gradients';
import GradientButton from './GradientButton';
import GradientText from './GradientText';
import { getSportsbookLogo, getSportsbookUrl } from '../data/logos';

const LegBetCard = ({ 
    option, 
    index, 
    isSelected, 
    onCardPress, 
    onMouseEnter, 
    onMouseLeave,
    onSelect,
    onOpenDeepLink,
    onWatchFixture, // Handler for watching "no odds yet" fixtures
    onUnwatchFixture, // Handler for unwatching fixtures
    isWatching = false, // Whether this fixture is currently being watched
    canShowMath,
    showAllMath = false,
    unitSize = 10,
    logoUrl,
    homeLogoUrl,
    awayLogoUrl,
    teamLogoUrl,
    sportsbookLogoUrl,
    kellyCriterion = 0.25, // Default to 1/4 Kelly if not provided
    userSelectedSportsbooks = [], // User's filtered sportsbooks from profile preferences
    marketPeers = [] // ⭐ All options in the same market (for devigged market average)
}) => {
    // Individual glow animation for this card (start at 1 for visibility on web)
    const glowT = useRef(new Animated.Value(1)).current;
    
    // State for showing math section and books list
    const [showMath, setShowMath] = useState(showAllMath);
    const [showBooks, setShowBooks] = useState(false);
    const [showBookDetails, setShowBookDetails] = useState(false);
    const [selectedBook, setSelectedBook] = useState('best'); // 'best' or specific book name
    const [sortBooksBy, setSortBooksBy] = useState('ev'); // 'ev' or 'odds' - controls sportsbook sorting within this leg
    
    // ⭐ Filter sportsbooks based on user preferences AND handle Blue Book → FanDuel mapping
    // Blue Book is the same as FanDuel, so:
    // - If FanDuel exists, filter out Blue Book
    // - If FanDuel doesn't exist but Blue Book does, rename Blue Book to FanDuel
    const filteredOption = React.useMemo(() => {
        let processedBookOdds = option?.bookOdds ? { ...option.bookOdds } : null;
        let processedAvailableBooks = option?.availableBooks ? [...option.availableBooks] : [];
        
        // 🔄 Handle Blue Book → FanDuel mapping
        if (processedBookOdds) {
            const blueBookKey = Object.keys(processedBookOdds).find(k => 
                k.toLowerCase().includes('blue') && k.toLowerCase().includes('book')
            );
            const fanDuelKey = Object.keys(processedBookOdds).find(k => 
                k.toLowerCase().includes('fanduel')
            );
            
            if (blueBookKey) {
                if (fanDuelKey) {
                    // FanDuel exists, just remove Blue Book
                    delete processedBookOdds[blueBookKey];
                } else {
                    // FanDuel doesn't exist, rename Blue Book to FanDuel
                    processedBookOdds['FanDuel'] = processedBookOdds[blueBookKey];
                    delete processedBookOdds[blueBookKey];
                }
            }
        }
        
        // Also update availableBooks array
        if (processedAvailableBooks.length > 0) {
            const hasFanDuel = processedAvailableBooks.some(b => b.toLowerCase().includes('fanduel'));
            const blueBookIdx = processedAvailableBooks.findIndex(b => 
                b.toLowerCase().includes('blue') && b.toLowerCase().includes('book')
            );
            
            if (blueBookIdx !== -1) {
                if (hasFanDuel) {
                    // Remove Blue Book since FanDuel exists
                    processedAvailableBooks.splice(blueBookIdx, 1);
                } else {
                    // Replace Blue Book with FanDuel
                    processedAvailableBooks[blueBookIdx] = 'FanDuel';
                }
            }
        }
        
        // Now apply user sportsbook filters if any
        if (!userSelectedSportsbooks || userSelectedSportsbooks.length === 0) {
            // No filter applied, return processed option
            return {
                ...option,
                bookOdds: processedBookOdds,
                availableBooks: processedAvailableBooks
            };
        }
        
        // Filter bookOdds to only include selected sportsbooks
        const filteredBookOdds = processedBookOdds ? 
            Object.fromEntries(
                Object.entries(processedBookOdds).filter(([bookName]) => 
                    userSelectedSportsbooks.includes(bookName)
                )
            ) : null;
        
        // Filter availableBooks array
        const filteredAvailableBooks = processedAvailableBooks.filter(bookName => 
            userSelectedSportsbooks.includes(bookName)
        );
        
        return {
            ...option,
            bookOdds: filteredBookOdds && Object.keys(filteredBookOdds).length > 0 ? filteredBookOdds : processedBookOdds,
            availableBooks: filteredAvailableBooks.length > 0 ? filteredAvailableBooks : processedAvailableBooks
        };
    }, [option, userSelectedSportsbooks]);
    
    // Use filtered option for all calculations
    const workingOption = filteredOption || option;
    
    // ⭐ Helper: Extract price from bookOdds (handles both old number format and new object format)
    const extractPrice = (bookOddsValue) => {
        if (bookOddsValue === null || bookOddsValue === undefined) return null;
        // New format: { price: -114, ev_percent: 2.5, ... }
        if (typeof bookOddsValue === 'object' && bookOddsValue.price !== undefined) {
            return bookOddsValue.price;
        }
        // Old format: just a number
        if (typeof bookOddsValue === 'number') return bookOddsValue;
        // Fallback: try to parse as string
        return parseFloat(String(bookOddsValue).replace(/[^-0-9.]/g, ''));
    };
    
    // Math inputs
    const [stake, setStake] = useState(unitSize);
    
    // ⭐ ALWAYS calculate best available odds (for display in "Best Available" row)
    const bestPrice = (() => {
        if (workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) {
            const allOdds = Object.values(workingOption.bookOdds)
                .map(extractPrice)
                .filter(o => o !== null && o !== undefined && !isNaN(o));
            if (allOdds.length > 0) {
                // Best odds = highest positive OR least negative (closest to 0)
                const best = allOdds.reduce((best, current) => {
                    // Positive odds: higher is better (+200 better than +150)
                    // Negative odds: closer to 0 is better (-110 better than -150)
                    if (current > 0 && best > 0) return current > best ? current : best;
                    if (current < 0 && best < 0) return current > best ? current : best;
                    if (current > 0) return current; // Positive always beats negative
                    if (best > 0) return best;
                    return current > best ? current : best;
                }, allOdds[0]);
                return best;
            }
        }
        // Fallback to raw.price if bookOdds not available
        const m = workingOption?.raw?.price ?? workingOption?.raw?.american_price ?? null;
        return typeof m === 'number' ? m : parseFloat(String(m || '').replace(/[^-0-9.]/g, ''));
    })();
    
    // ⭐ Calculate best available EV% (for "Best Available" row when sorting by EV)
    const bestEvData = (() => {
        if (workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) {
            let bestEv = null;
            let bestEvOdds = null;
            let bestEvBook = null;
            
            Object.entries(workingOption.bookOdds).forEach(([bookName, bookData]) => {
                const ev = typeof bookData === 'object' ? bookData.ev_percent : null;
                const odds = extractPrice(bookData);
                if (ev !== null && ev !== undefined && (bestEv === null || ev > bestEv)) {
                    bestEv = ev;
                    bestEvOdds = odds;
                    bestEvBook = bookName;
                }
            });
            
            return { ev: bestEv, odds: bestEvOdds, book: bestEvBook };
        }
        return { ev: null, odds: null, book: null };
    })();
    
    // Price for calculations - uses selected book if chosen, otherwise best available
    const price = (() => {
        // If specific book selected, use that book's odds
        if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
            return extractPrice(workingOption.bookOdds[selectedBook]);
        }
        // Otherwise use best available
        return bestPrice;
    })();
    const implied = (() => {
        if (price === null || isNaN(price)) return null;
        if (price > 0) return 100 / (price + 100);
        return (-price) / (-price + 100);
    })();
    const stakeNum = typeof stake === 'number' ? stake : (parseFloat(String(stake || '0')) || 0);
    const payout = (() => {
        if (price === null || isNaN(price)) return null;
        if (price > 0) return stakeNum * (price / 100) + stakeNum;
        return stakeNum * (100 / (-price)) + stakeNum;
    })();
    const aiProb = (() => {
        let rawAiProb = null;
        if (typeof option?.raw?.ai_prob === 'number') {
            rawAiProb = option.raw.ai_prob;
        } else {
            // If an AI fair price were ever included in raw, convert to probability
            const fairAmerican = option?.raw?.ai_price_american;
            if (typeof fairAmerican === 'number') {
                rawAiProb = fairAmerican > 0 ? 100 / (fairAmerican + 100) : (-fairAmerican) / (-fairAmerican + 100);
            }
        }
        
        if (rawAiProb === null) return null;
        
        // FIX: Check if ai_prob is for the opposite side of the bet
        // If book odds imply >50% but ai_prob is <50% (or vice versa), flip it
        if (implied !== null) {
            const impliedIsFavorite = implied > 0.5;
            const aiProbIsFavorite = rawAiProb > 0.5;
            
            // If they disagree on which side is favored, ai_prob is likely for the opposite side
            if (impliedIsFavorite !== aiProbIsFavorite) {
                // Additional check: if the difference is large (>30%), flip it
                const diff = Math.abs(implied - rawAiProb);
                if (diff > 0.3) {
                    console.log('[LegBetCard] Flipping ai_prob - opposite side detected', {
                        selection: option?.description,
                        price,
                        implied: implied.toFixed(3),
                        rawAiProb: rawAiProb.toFixed(3),
                        flippedAiProb: (1 - rawAiProb).toFixed(3)
                    });
                    return 1 - rawAiProb;
                }
            }
        }
        
        return rawAiProb;
    })();
    // Calculate all 3 devigging methods on-the-fly (if not cached)
    const devigMethods = (() => {
        // ⭐ PRIORITY 1: If a specific sportsbook is selected, use its EV data from bookOdds
        if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
            const bookData = workingOption.bookOdds[selectedBook];
            // New format: bookOdds[book] is an object with full EV data
            if (typeof bookData === 'object' && bookData.devig_methods) {
                return {
                    additive: {
                        fairProb: bookData.devig_methods.additive_prob,
                        ev: bookData.ev_method1_additive
                    },
                    multiplicative: {
                        fairProb: bookData.devig_methods.multiplicative_prob,
                        ev: bookData.ev_method2_multiplicative
                    },
                    power: bookData.ev_method3_power !== undefined ? {
                        fairProb: bookData.devig_methods.power_prob,
                        ev: bookData.ev_method3_power
                    } : null,
                    methodUsed: bookData.ev_method_used
                };
            }
        }
        
        // ⭐ PRIORITY 2: If "best" is selected, use cached devigged data from raw
        if (workingOption?.raw?.devig_methods && workingOption?.raw?.ev_method1_additive !== undefined) {
            return {
                additive: {
                    fairProb: workingOption.raw.devig_methods.additive_prob,
                    ev: workingOption.raw.ev_method1_additive
                },
                multiplicative: {
                    fairProb: workingOption.raw.devig_methods.multiplicative_prob,
                    ev: workingOption.raw.ev_method2_multiplicative
                },
                power: workingOption.raw.ev_method3_power !== undefined ? {
                    fairProb: workingOption.raw.devig_methods.power_prob,
                    ev: workingOption.raw.ev_method3_power
                } : null,
                methodUsed: workingOption.raw.ev_method_used
            };
        }
        
        // Calculate on-the-fly if no cached data
        if (implied === null) return null;
        
        // For single-odd calculation, we assume 2-way market with opponent
        const impliedProb = implied;
        const oppositeProb = 1 - impliedProb;
        const totalImplied = impliedProb + oppositeProb; // Should be ~1.0 for fair market, >1.0 with vig
        const overround = totalImplied - 1;
        
        if (overround < 0 || overround > 0.5) {
            // Extreme vig or invalid, fallback to simple calc
            return null;
        }
        
        // METHOD 1: Additive
        const fairProb_additive = impliedProb / totalImplied;
        const ev_additive = ((fairProb_additive * (price > 0 ? (price/100 + 1) : (100/-price + 1))) - 1) * 100;
        
        // METHOD 2: Multiplicative (same as additive for 2-way)
        const fairProb_multiplicative = impliedProb / totalImplied;
        const ev_multiplicative = ((fairProb_multiplicative * (price > 0 ? (price/100 + 1) : (100/-price + 1))) - 1) * 100;
        
        // METHOD 3: Power (iterative)
        const n = 2; // Assume 2-way market
        const R = 1 / (1 + overround);
        let k = Math.log(n * R) / Math.log(n);
        
        // Iterate to find k (max 20 iterations for performance)
        const maxIter = 20;
        const tolerance = 0.0001;
        let fairProb_power = impliedProb;
        
        for (let iter = 0; iter < maxIter; iter++) {
            const powered1 = Math.pow(impliedProb, k);
            const powered2 = Math.pow(oppositeProb, k);
            const sumPowered = powered1 + powered2;
            
            fairProb_power = powered1 / sumPowered;
            const checkSum = fairProb_power + (powered2 / sumPowered);
            
            if (Math.abs(checkSum - 1.0) < tolerance) {
                break;
            }
            
            k *= (1 + (1 - checkSum) * 0.1);
        }
        
        const ev_power = ((fairProb_power * (price > 0 ? (price/100 + 1) : (100/-price + 1))) - 1) * 100;
        
        // Return most conservative (lowest) EV%
        const evMin = Math.min(ev_additive, ev_multiplicative, ev_power);
        const methodUsed = evMin === ev_additive ? 'additive' : evMin === ev_multiplicative ? 'multiplicative' : 'power';
        
        return {
            additive: { fairProb: fairProb_additive, ev: ev_additive },
            multiplicative: { fairProb: fairProb_multiplicative, ev: ev_multiplicative },
            power: { fairProb: fairProb_power, ev: ev_power },
            methodUsed
        };
    })();
    
    const ev = (() => {
        // ⭐ PRIORITY 1: If a specific sportsbook is selected, use its EV from bookOdds
        if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
            const bookData = workingOption.bookOdds[selectedBook];
            // New format: bookOdds[book] is an object with full EV data
            if (typeof bookData === 'object' && bookData.ev_percent !== undefined && bookData.ev_percent !== null) {
                return bookData.ev_percent;
            }
        }
        
        // ⭐ PRIORITY 2: Use backend's properly devigged EV% (for "best" book)
        if (workingOption?.raw?.ev_percent !== undefined && workingOption?.raw?.ev_percent !== null) {
            return workingOption.raw.ev_percent;
        }
        
        // PRIORITY 3: Use calculated devigged methods
        if (devigMethods) {
            return Math.min(devigMethods.additive.ev, devigMethods.multiplicative.ev, devigMethods.power?.ev || Infinity);
        }
        
        // PRIORITY 4: Fallback to simple calculation
        if (aiProb === null || implied === null) return null;
        const edge = aiProb - implied;
        return edge * 100;
    })();

    // ⭐ TRUE DEVIGGED MARKET AVERAGE - Averages implied probabilities across all books, then devigs
    const marketAverage = (() => {
        // Helper to convert American odds to implied probability
        const toImpliedProb = (american) => {
            if (american > 0) return 100 / (american + 100);
            return (-american) / (-american + 100);
        };
        
        // Helper to get ALL prices from bookOdds as array
        const getAllPrices = (bookOdds) => {
            if (!bookOdds || typeof bookOdds !== 'object') return [];
            return Object.values(bookOdds)
                .map(o => {
                    if (typeof o === 'object' && o !== null && o.price !== undefined) return o.price;
                    if (typeof o === 'number') return o;
                    return null;
                })
                .filter(p => p !== null && !isNaN(p));
        };
        
        // Get all prices for current selection
        const currentPrices = getAllPrices(workingOption?.bookOdds);
        if (currentPrices.length === 0) return null;
        
        // ⭐ Find opposite selection from marketPeers
        const currentSelection = (workingOption?.selection || workingOption?.raw?.selection || '').toLowerCase();
        const oppositeOption = marketPeers.find(peer => {
            const peerSelection = (peer?.selection || peer?.raw?.selection || '').toLowerCase();
            return peerSelection !== currentSelection && peerSelection.length > 0;
        });
        
        // If we found opposite side, calculate TRUE devigged market average
        if (oppositeOption) {
            const oppPrices = getAllPrices(oppositeOption?.bookOdds);
            if (oppPrices.length > 0) {
                // ⭐ Step 1: Convert all odds to implied probabilities
                const currentImpliedProbs = currentPrices.map(toImpliedProb);
                const oppImpliedProbs = oppPrices.map(toImpliedProb);
                
                // ⭐ Step 2: Average the implied probabilities for each side
                const avgCurrentImplied = currentImpliedProbs.reduce((sum, p) => sum + p, 0) / currentImpliedProbs.length;
                const avgOppImplied = oppImpliedProbs.reduce((sum, p) => sum + p, 0) / oppImpliedProbs.length;
                
                // ⭐ Step 3: Calculate total (includes vig)
                const totalImplied = avgCurrentImplied + avgOppImplied;
                
                // ⭐ Step 4: Remove vig - fair probability = avgImplied / totalImplied
                const fairProb = avgCurrentImplied / totalImplied;
                
                // ⭐ Step 5: Convert fair probability to American odds
                if (fairProb >= 0.5) {
                    return Math.round(-100 * fairProb / (1 - fairProb));
                } else {
                    return Math.round(100 * (1 - fairProb) / fairProb);
                }
            }
        }
        
        // Fallback: average implied probabilities for just this side (not devigged, but better than nothing)
        if (currentPrices.length > 0) {
            const impliedProbs = currentPrices.map(toImpliedProb);
                const avgProb = impliedProbs.reduce((sum, prob) => sum + prob, 0) / impliedProbs.length;
                
                if (avgProb >= 0.5) {
                    return Math.round(-100 * avgProb / (1 - avgProb));
                } else {
                    return Math.round(100 * (1 - avgProb) / avgProb);
            }
        }
        return null;
    })();

    const marketAverageImplied = (() => {
        if (marketAverage === null) return null;
        if (marketAverage > 0) return 100 / (marketAverage + 100);
        return (-marketAverage) / (-marketAverage + 100);
    })();

    const fairPrice = (() => {
        // Use AI probability to calculate fair price
        if (aiProb === null) return null;
        if (aiProb >= 0.5) return Math.round(-100 / (aiProb - 1));
        return Math.round(100 / aiProb - 100);
    })();

    const fairPriceImplied = (() => {
        if (fairPrice === null) return null;
        if (fairPrice > 0) return 100 / (fairPrice + 100);
        return (-fairPrice) / (-fairPrice + 100);
    })();

    // Kelly Criterion calculation
    const kellyUnits = (() => {
        if (aiProb === null || implied === null) return null;
        const b = price > 0 ? (price / 100) : (100 / (-price)); // Decimal odds
        const p = aiProb; // Our probability
        const q = 1 - p; // Probability of losing
        const fullKelly = (b * p - q) / b; // Calculate Full Kelly
        const kelly = fullKelly * kellyCriterion; // Apply fractional Kelly (e.g., 1/4 Kelly = 0.25)
        
        // Only return positive Kelly values (don't bet on negative EV)
        // Cap at 50% of bankroll for safety (even full Kelly shouldn't exceed this)
        return Math.max(0, Math.min(kelly, 0.5));
    })();

    const suggestedWager = kellyUnits !== null && kellyUnits > 0 ? Math.max(1, Math.round(stakeNum * kellyUnits)) : null;

    // Book count and individual book data from bookOdds
    const bookOddsData = (() => {
        // Priority 1: Use bookOdds object if available
        if (workingOption?.bookOdds && typeof workingOption.bookOdds === 'object') {
            return Object.entries(workingOption.bookOdds)
                .filter(([book, odds]) => odds !== null && odds !== undefined)
                .map(([book, odds]) => ({ book, odds }));
        }
        
        // Priority 2: Use raw.bookOdds if available
        if (workingOption?.raw?.bookOdds && typeof workingOption.raw.bookOdds === 'object') {
            return Object.entries(workingOption.raw.bookOdds)
                .filter(([book, odds]) => odds !== null && odds !== undefined)
                .map(([book, odds]) => ({ book, odds }));
        }
        
        // Priority 3: Fallback to availableBooks (legacy)
        if (workingOption?.availableBooks && Array.isArray(workingOption.availableBooks)) {
            return workingOption.availableBooks.map(book => ({ book, odds: null }));
        }
        
        return [];
    })();
    
    const bookCount = bookOddsData.length;
    const [showAllBooks, setShowAllBooks] = useState(false);
    const displayedBooks = showAllBooks ? bookOddsData : bookOddsData.slice(0, 5);

    const handleMouseEnter = () => {
        Animated.timing(glowT, { 
            toValue: 1, 
            duration: 1500, 
            easing: Easing.bezier(0.22, 1, 0.36, 1), 
            useNativeDriver: true 
        }).start();
        
        if (onMouseEnter) onMouseEnter();
    };

    const handleMouseLeave = () => {
        if (!isSelected) {
            Animated.timing(glowT, { 
                toValue: 0, 
                duration: 1500, 
                easing: Easing.bezier(0.22, 1, 0.36, 1), 
                useNativeDriver: true 
            }).start();
        }
        
        if (onMouseLeave) onMouseLeave();
    };

    const handleCardPress = () => {
        if (onSelect) {
            onSelect(option);
        } else if (onCardPress) {
            onCardPress(index);
        }
    };

    const handleSelectLeg = () => {
        if (onSelect) {
            onSelect(option);
        } else if (onCardPress) {
            onCardPress(index);
        }
    };

    const handleToggleMath = () => {
        setShowMath(!showMath);
    };
    
    // Check if this is a "no odds yet" fixture card
    const isNoOddsYet = option?._noOddsYet === true;
    const isLive = option?._isLive === true;
    const isEnded = option?._isEnded === true;

    // ⭐ Helper: Get the DISPLAY EV for the three methods (fall back to main EV if methods are null or zero)
    const displayEvMethods = React.useMemo(() => {
        // If devigMethods has valid NON-ZERO data, use it
        // Check for actual non-zero EV values (0.00% is likely a failed calculation)
        const hasValidDevigMethods = devigMethods && 
            devigMethods.additive?.ev !== undefined && 
            (Math.abs(devigMethods.additive.ev) > 0.001 || Math.abs(devigMethods.multiplicative?.ev || 0) > 0.001);
        
        if (hasValidDevigMethods) {
            return devigMethods;
        }
        
        // If we have a real ev_percent from backend (non-zero), use it for all three as a fallback
        // This handles cases where the on-the-fly calculation fails (non-2-way markets like player props)
        if (ev !== null && ev !== undefined && Math.abs(ev) > 0.001) {
            return {
                additive: { fairProb: null, ev: ev },
                multiplicative: { fairProb: null, ev: ev },
                power: { fairProb: null, ev: ev },
                methodUsed: workingOption?.raw?.ev_method_used || 'power'
            };
        }
        
        // If devigMethods exists but has zero values, still return it (actual zero EV)
        if (devigMethods && devigMethods.additive?.ev !== undefined) {
            return devigMethods;
        }
        
        return null;
    }, [devigMethods, ev, workingOption?.raw?.ev_method_used]);

    return (
        <Pressable 
            style={[
                styles.betCard, 
                isSelected && styles.selectedBetCard,
                // Yellow border for upcoming games
                isNoOddsYet && !isLive && !isEnded && { opacity: 0.7, borderColor: 'rgba(255, 193, 7, 0.4)', borderWidth: 1 },
                // Green border for live games
                isLive && { borderColor: 'rgba(76, 175, 80, 0.6)', borderWidth: 2 },
                // Grey border for ended games
                isEnded && { opacity: 0.6, borderColor: 'rgba(158, 158, 158, 0.4)', borderWidth: 1 },
                // Thicker border when selected
                isSelected && !isLive && !isEnded && { 
                    borderWidth: 3,
                    borderColor: '#A259FF',
                    shadowColor: '#A259FF',
                    shadowOpacity: 0.3,
                    shadowRadius: 12,
                    elevation: 8
                }
            ]} 
            testID="bet-card-pressable"
            onPress={isNoOddsYet ? undefined : handleCardPress} // Disable press for "no odds yet" cards
            {...(Platform.OS === 'web' && !isNoOddsYet ? { 
                onMouseEnter: handleMouseEnter, 
                onMouseLeave: handleMouseLeave
            } : {})}
        >
            {/* ⭐ TWO-COLUMN LAYOUT: Left (info+math+buttons), Right (sportsbooks full height) */}
            <View style={[styles.betCardContent, { opacity: 1 }]}>
                <View style={styles.twoColumnLayout}>
                    {/* LEFT COLUMN: Bet Info + Math + Action Buttons */}
                    <View style={styles.leftColumn}>
                        {/* ROW 1: Title, Description, Date/Time, Logos */}
                        <View style={styles.infoSection}>
                            {/* Title and Description */}
                            <View style={[styles.betCardTitle, { opacity: 1 }]}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {/* ⭐ LARGER TITLE TEXT */}
                                    <Text style={[styles.betCardTitleTextLarge, { opacity: isNoOddsYet ? 0.6 : 1 }]}>{option.title}</Text>
                                    {(() => {
                                        // Determine which book to display
                                        let displayBook = selectedBook;
                                        
                                        // If "best" is selected, find which book has the best odds
                                        if (selectedBook === 'best' && workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) {
                                            let bestBook = null;
                                            let bestOdds = null;
                                            
                                            Object.entries(workingOption.bookOdds).forEach(([bookName, bookOddsValue]) => {
                                                const odds = extractPrice(bookOddsValue);
                                                if (odds !== null && odds !== undefined && !isNaN(odds)) {
                                                    if (bestOdds === null) {
                                                        bestBook = bookName;
                                                        bestOdds = odds;
                                                    } else {
                                                        // Best odds = highest positive OR least negative (closest to 0)
                                                        if (odds > 0 && bestOdds > 0) {
                                                            if (odds > bestOdds) {
                                                                bestBook = bookName;
                                                                bestOdds = odds;
                                                            }
                                                        } else if (odds < 0 && bestOdds < 0) {
                                                            if (odds > bestOdds) {
                                                                bestBook = bookName;
                                                                bestOdds = odds;
                                                            }
                                                        } else if (odds > 0) {
                                                            bestBook = bookName;
                                                            bestOdds = odds;
                                                        }
                                                    }
                                                }
                                            });
                                            
                                            displayBook = bestBook;
                                        }
                                        
                                        // Show the purple badge with the book name
                                        if (displayBook && displayBook !== 'best') {
                                            return (
                                                <View style={{
                                                    backgroundColor: 'rgba(162, 89, 255, 0.25)',
                                                    paddingHorizontal: 8,
                                                    paddingVertical: 3,
                                                    borderRadius: 10,
                                                    borderWidth: 1,
                                                    borderColor: 'rgba(162, 89, 255, 0.6)',
                                                }}>
                                            <Text style={{ color: 'rgba(162, 89, 255, 0.95)', fontSize: 11, fontWeight: '700' }}>
                                                        📍 {displayBook}
                                                    </Text>
                                                </View>
                                            );
                                        }
                                        
                                        return null;
                                    })()}
                                        </View>
                                {/* ⭐ LARGER DESCRIPTION TEXT */}
                                <Text style={[styles.betCardDescriptionLarge, { opacity: isNoOddsYet ? 0.6 : 1 }]}>
                                            {option.description}
                                        </Text>
                            </View>
                            
                            {/* Date and Time Display */}
                          {(() => {
                            const gameDate = option?.date || option?.raw?.commence_time || option?.raw?.event_date || option?.raw?.game_time || option?.raw?.start_date || option?.raw?.start_time || '';
                            if (!gameDate) return null;
                            
                            try {
                              const d = new Date(gameDate);
                              if (isNaN(d.getTime())) return null;
                              
                              const now = new Date();
                              const isToday = d.toDateString() === now.toDateString();
                              const tomorrow = new Date(now);
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              const isTomorrow = d.toDateString() === tomorrow.toDateString();
                              const isPast = d < now;
                              
                              let dateLabel = '';
                              if (isToday) dateLabel = 'Today';
                              else if (isTomorrow) dateLabel = 'Tomorrow';
                              else dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                              
                              const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              
                                  // Status badge color and text
                              let badgeColor, badgeBorder, badgeText, badgeEmoji, showBadge = false;
                              if (isEnded) {
                                badgeColor = 'rgba(158, 158, 158, 0.2)';
                                badgeBorder = 'rgba(158, 158, 158, 0.5)';
                                badgeText = '#9E9E9E';
                                badgeEmoji = '⏹';
                                dateLabel = 'Ended';
                                showBadge = true;
                              } else if (isLive) {
                                badgeColor = 'rgba(76, 175, 80, 0.25)';
                                badgeBorder = 'rgba(76, 175, 80, 0.6)';
                                badgeText = '#4CAF50';
                                badgeEmoji = '🔴';
                                dateLabel = isPast ? 'Started at' : dateLabel;
                                showBadge = true;
                              } else if (isNoOddsYet) {
                                badgeColor = 'rgba(255, 193, 7, 0.2)';
                                badgeBorder = 'rgba(255, 193, 7, 0.5)';
                                badgeText = '#FFC107';
                                badgeEmoji = '🕐';
                                showBadge = true;
                              }
                              
                              return (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                    {showBadge && (
                                      <View style={{
                                        backgroundColor: badgeColor,
                                        paddingHorizontal: 8,
                                        paddingVertical: 3,
                                        borderRadius: 10,
                                        borderWidth: 1,
                                        borderColor: badgeBorder,
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 4,
                                      }}>
                                          <Text style={{ fontSize: 12 }}>{badgeEmoji}</Text>
                                        <Text style={{ color: badgeText, fontSize: 10, fontWeight: '700' }}>
                                          {isEnded ? 'Ended' : (isLive ? 'LIVE' : 'Soon')}
                                        </Text>
                                      </View>
                                    )}
                                    <Text style={{ color: 'rgba(162, 89, 255, 0.95)', fontSize: 13, fontWeight: '700' }}>{dateLabel}</Text>
                                      {!isEnded && <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12, fontWeight: '500' }}>{timeStr}</Text>}
                                </View>
                              );
                            } catch {
                              return null;
                            }
                          })()}
                          
                          {/* Team Logos */}
                            {(homeLogoUrl || awayLogoUrl || teamLogoUrl) && (
                              <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                                {awayLogoUrl ? (<Image source={{ uri: awayLogoUrl }} style={{ width: 36, height: 36, borderRadius: 6 }} resizeMode="contain" />) : null}
                                {homeLogoUrl ? (<Image source={{ uri: homeLogoUrl }} style={{ width: 36, height: 36, borderRadius: 6 }} resizeMode="contain" />) : null}
                                {teamLogoUrl && !homeLogoUrl && !awayLogoUrl ? (<Image source={{ uri: teamLogoUrl }} style={{ width: 36, height: 36, borderRadius: 6 }} resizeMode="contain" />) : null}
                              </View>
                                )}
                              </View>
                        
                        {/* ROW 2: Math Section (inside left column) */}
                    {canShowMath && showMath && (
                            <View style={[styles.mathSectionInline, { opacity: 1 }]}>
                                {/* EV% Devigging Methods */}
                                {displayEvMethods && (
                                <View style={[styles.metricsRow, { marginBottom: 12, opacity: 1 }]}>
                                    <View style={[styles.metricCol, styles.metricColFirst, { opacity: 1 }]}>
                                        <Text style={[styles.metricLabel, { opacity: 1 }]}>Additive EV%</Text>
                                        <Text style={[
                                            styles.metricValue, 
                                                (displayEvMethods.additive?.ev || 0) > 0 ? styles.evPositive : styles.evNegative,
                                            { opacity: 1 }
                                        ]}>
                                                {displayEvMethods.additive?.ev != null ? displayEvMethods.additive.ev.toFixed(2) : (ev != null ? ev.toFixed(2) : 'N/A')}%
                                        </Text>
                                            {displayEvMethods.methodUsed === 'additive' && (
                                            <Text style={[styles.metricSubtext, { color: '#4ADE80', opacity: 1 }]}>✓ Shown</Text>
                                        )}
                                    </View>
                                    <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                        <Text style={[styles.metricLabel, { opacity: 1 }]}>Mult. EV%</Text>
                                        <Text style={[
                                            styles.metricValue, 
                                                (displayEvMethods.multiplicative?.ev || 0) > 0 ? styles.evPositive : styles.evNegative,
                                            { opacity: 1 }
                                        ]}>
                                                {displayEvMethods.multiplicative?.ev != null ? displayEvMethods.multiplicative.ev.toFixed(2) : (ev != null ? ev.toFixed(2) : 'N/A')}%
                                        </Text>
                                            {displayEvMethods.methodUsed === 'multiplicative' && (
                                            <Text style={[styles.metricSubtext, { color: '#4ADE80', opacity: 1 }]}>✓ Shown</Text>
                                        )}
                                    </View>
                                    <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                        <Text style={[styles.metricLabel, { opacity: 1 }]}>Power EV%</Text>
                                        <Text style={[
                                            styles.metricValue, 
                                                displayEvMethods.power ? ((displayEvMethods.power.ev || 0) > 0 ? styles.evPositive : styles.evNegative) : { opacity: 1 },
                                            { opacity: 1 }
                                        ]}>
                                                {displayEvMethods.power?.ev != null ? displayEvMethods.power.ev.toFixed(2) : (ev != null ? ev.toFixed(2) : 'N/A')}%
                                        </Text>
                                            {displayEvMethods.methodUsed === 'power' && (
                                            <Text style={[styles.metricSubtext, { color: '#4ADE80', opacity: 1 }]}>✓ Shown</Text>
                                        )}
                                    </View>
                                </View>
                            )}
                            
                                {/* Price and Fair Value Row */}
                            <View style={[styles.metricsRow, { opacity: 1 }]}>
                                <View style={[styles.metricCol, styles.metricColFirst, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>
                                        {selectedBook === 'best' ? 'Best Odds' : `${selectedBook} Odds`}
                                    </Text>
                                    <Text style={[styles.metricValue, { opacity: 1 }]}>{price !== null ? `${price > 0 ? '+' : ''}${price}` : '—'}</Text>
                                    <Text style={[styles.metricSubtext, { opacity: 1 }]}>{implied !== null ? `${(implied * 100).toFixed(1)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>Market Average</Text>
                                    <Text style={[styles.metricValue, { opacity: 1 }]}>{marketAverage !== null ? `${marketAverage > 0 ? '+' : ''}${marketAverage}` : '—'}</Text>
                                    <Text style={[styles.metricSubtext, { opacity: 1 }]}>{marketAverageImplied !== null ? `${(marketAverageImplied * 100).toFixed(1)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>EV%</Text>
                                        <Text style={[styles.metricValue, ev !== null ? (ev >= 0 ? styles.evPositive : styles.evNegative) : null, { opacity: 1 }]}>{ev !== null ? `${ev >= 0 ? '+' : ''}${ev.toFixed(2)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>Books</Text>
                                    <Text style={[styles.metricValue, { opacity: 1 }]}>{bookCount}</Text>
                                </View>
                            </View>

                                {/* Kelly Criterion Row */}
                            <View style={[styles.metricsRow, { marginTop: 12, opacity: 1 }]}>
                                <View style={[styles.metricCol, styles.metricColFirst, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>Kelly Units</Text>
                                    <Text style={[styles.metricValue, { opacity: 1 }]}>{kellyUnits !== null ? `${(kellyUnits * 100).toFixed(1)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>Suggested Wager</Text>
                                    <Text style={[styles.metricValue, { opacity: 1 }]}>{suggestedWager !== null ? `$${suggestedWager}` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>Your Stake</Text>
                                    <TextInput
                                        style={styles.stakeInputInline}
                                        value={String(stakeNum)}
                                        onChangeText={(t) => {
                                            const n = parseFloat(String(t).replace(/[^0-9.]/g, ''));
                                            if (!isNaN(n)) setStake(n); else if (t === '') setStake(0);
                                        }}
                                        keyboardType="numeric"
                                        placeholder="10"
                                        placeholderTextColor="rgba(255,255,255,0.5)"
                                    />
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1 }]}>Payout</Text>
                                    <Text style={[styles.metricValue, { opacity: 1 }]}>{payout !== null ? `$${payout.toFixed(2)}` : '—'}</Text>
                                </View>
                            </View>

                            {/* Quick stake buttons */}
                            <View style={[styles.stakeQuickRow, { marginTop: 8, opacity: 1 }]}>
                                {[5, 10, 25, 50].map((amt) => (
                                    <TouchableOpacity key={amt} onPress={() => setStake(amt)} style={[styles.stakeChip, stakeNum === amt ? styles.stakeChipActive : null, { opacity: 1 }]}>
                                        <Text style={[styles.stakeChipText, stakeNum === amt ? styles.stakeChipTextActive : null, { opacity: 1 }]}>${amt}</Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                                        </View>
                        )}
                        
                        {/* ROW 3: Action Buttons (inside left column) */}
                        {!isNoOddsYet && (
                            <View style={styles.betCardActionsInline} onStartShouldSetResponder={() => true} onTouchEnd={(e) => e.stopPropagation()}>
                                <View>
                                    <GradientButton
                                        text={isSelected ? "Deselect Leg" : "Select Leg"}
                                        onPress={(e) => { e?.stopPropagation?.(); handleSelectLeg(); }}
                                        style={styles.betCardButton}
                                    />
                                </View>
                                {/* Deep Link Button - uses selected book's deep link or fallback to primary */}
                                {(() => {
                                    // ⭐ Helper: Extract URL from deep_link (handles both string and object format)
                                    const extractDeepLink = (dl) => {
                                        if (!dl) return null;
                                        if (typeof dl === 'string') return dl;
                                        // Object format: { android, desktop, ios }
                                        if (typeof dl === 'object') {
                                            // For web, prefer desktop URL
                                            return dl.desktop || dl.ios || dl.android || null;
                                        }
                                        return null;
                                    };
                                    
                                    // Try to get deep link from selected sportsbook first
                                    let deepLink = null;
                                    let displayBookName = selectedBook;
                                    let isDirectLink = false; // ⭐ Track if it's a real deep link vs fallback
                                    
                                    if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
                                        const bookData = workingOption.bookOdds[selectedBook];
                                        if (typeof bookData === 'object') {
                                            deepLink = extractDeepLink(bookData.deep_link) || extractDeepLink(bookData.deeplink) || bookData.url;
                                            if (deepLink) isDirectLink = true;
                                        }
                                    }
                                    
                                    // If no book-specific link, find best book's link or use primary
                                    if (!deepLink && selectedBook === 'best' && workingOption?.bookOdds) {
                                        // Find best book and its deep link
                                        let bestBook = null;
                                        let bestOdds = null;
                                        Object.entries(workingOption.bookOdds).forEach(([bookName, bookData]) => {
                                            const odds = extractPrice(bookData);
                                            if (odds !== null && (bestOdds === null || odds > bestOdds)) {
                                                bestOdds = odds;
                                                bestBook = bookName;
                                                if (typeof bookData === 'object') {
                                                    const foundLink = extractDeepLink(bookData.deep_link) || extractDeepLink(bookData.deeplink) || bookData.url;
                                                    if (foundLink) {
                                                        deepLink = foundLink;
                                                        isDirectLink = true;
                                                    }
                                                }
                                            }
                                        });
                                        displayBookName = bestBook || 'Sportsbook';
                                    }
                                    
                                    // Fallback to primary URLs
                                    if (!deepLink) {
                                        const urls = option?.raw?.urls || {};
                                        deepLink = urls.offer || urls.deeplink || urls.event || urls.market || null;
                                        if (deepLink) isDirectLink = true;
                                    }
                                    
                                    // ⭐ FALLBACK: Use sportsbook homepage URL if no deep link available
                                    if (!deepLink) {
                                        const fallbackUrl = getSportsbookUrl(displayBookName);
                                        if (fallbackUrl) {
                                            deepLink = fallbackUrl;
                                            isDirectLink = false; // This is NOT a direct link to bet
                                            console.log('[DeepLink] Using sportsbook homepage fallback for:', displayBookName, fallbackUrl);
                                        }
                                    }
                                    
                                    // Debug: Log what we found
                                    if (!deepLink && workingOption?.bookOdds) {
                                        const firstBook = Object.entries(workingOption.bookOdds)[0];
                                        if (firstBook) {
                                            console.log('[DeepLink] No deepLink found. First book data:', firstBook[0], firstBook[1]?.deep_link);
                                        }
                                    }
                                    
                                    if (!deepLink) return null;
                                    
                                    const bookDisplayText = displayBookName !== 'best' ? displayBookName : 'Sportsbook';
                                    // ⭐ Different text for direct links vs fallback homepage links
                                    const buttonText = isDirectLink 
                                        ? `Place Bet on ${bookDisplayText}` 
                                        : `Visit ${bookDisplayText}`;
                                    
                                    console.log('[DeepLink] 🔗 Rendering button for:', displayBookName, '| isDirectLink:', isDirectLink, '| URL:', typeof deepLink === 'object' ? JSON.stringify(deepLink) : deepLink);
                                    
                                    return (
                                      <View>
                                        <GradientButton
                                          text={buttonText}
                                          onPress={(e) => {
                                            console.log('[DeepLink] 🖱️ Button PRESSED!', {
                                              deepLink,
                                              selectedBook,
                                              displayBookName,
                                              isDirectLink,
                                              hasOnOpenDeepLink: !!onOpenDeepLink
                                            });
                                            e?.stopPropagation?.();
                                            if (onOpenDeepLink) { 
                                              console.log('[DeepLink] 📤 Calling onOpenDeepLink handler...');
                                              onOpenDeepLink({ ...option, _selectedBook: selectedBook, _deepLink: deepLink, _isDirectLink: isDirectLink }); 
                                              return; 
                                            }
                                            // Open deep link URL in new tab (fallback if no handler)
                                            console.log('[DeepLink] 🌐 Opening directly (no handler)...');
                                            let finalUrl = deepLink;
                                            if (typeof deepLink === 'object') {
                                              finalUrl = deepLink.desktop || deepLink.ios || deepLink.android;
                                            }
                                            if (typeof finalUrl === 'string' && finalUrl.startsWith('http')) {
                                              window.open(finalUrl, '_blank', 'noopener,noreferrer');
                                            } else {
                                              console.warn('[DeepLink] ⚠️ Invalid URL:', finalUrl);
                                            }
                                          }}
                                          style={[
                                            styles.betCardButton,
                                            // ⭐ Subtle visual differentiation for fallback links - dotted border
                                            !isDirectLink && { 
                                              borderStyle: 'dashed',
                                              borderColor: 'rgba(255, 255, 255, 0.25)',
                                            }
                                          ]}
                                          // ⭐ Muted colors for fallback links vs bright for direct links
                                          gradientColors={isDirectLink 
                                            ? ["#10B981", "#34D399"]  // Green gradient for direct bet links
                                            : ["#6B7280", "#9CA3AF"]  // Gray gradient for homepage fallbacks
                                          }
                                        />
                                      </View>
                                    );
                                })()}
                                {canShowMath ? (
                                  <View>
                                      <GradientButton
                                          text={showMath ? "Hide Math" : "Show Math"}
                                          onPress={(e) => { e?.stopPropagation?.(); handleToggleMath(); }}
                                          style={styles.betCardButton}
                                      />
                                  </View>
                                ) : null}
                        </View>
                    )}
                    
                        {/* Watch button for "no odds yet" cards */}
                    {isNoOddsYet && !isLive && !isEnded && (
                            <View style={{ paddingVertical: 12, alignItems: 'flex-start' }}>
                            {isWatching ? (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                        <View style={{
                                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                            paddingHorizontal: 12,
                                            paddingVertical: 6,
                                            borderRadius: 16,
                                            borderWidth: 1,
                                            borderColor: 'rgba(76, 175, 80, 0.5)',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 6,
                                        }}>
                                            <Text style={{ fontSize: 16 }}>👁️</Text>
                                            <Text style={{ color: '#4CAF50', fontSize: 12, fontWeight: '700' }}>Watching</Text>
                                        </View>
                                        <TouchableOpacity
                                            onPress={(e) => {
                                                e?.stopPropagation?.();
                                                if (onUnwatchFixture) {
                                                    onUnwatchFixture(option);
                                                }
                                            }}
                                            style={{
                                                backgroundColor: 'rgba(244, 67, 54, 0.25)',
                                                paddingHorizontal: 12,
                                                paddingVertical: 6,
                                                borderRadius: 16,
                                                borderWidth: 1,
                                                borderColor: 'rgba(244, 67, 54, 0.6)',
                                            }}
                                        >
                                            <Text style={{ color: '#FF5252', fontSize: 12, fontWeight: '700' }}>
                                                Stop Watching
                                            </Text>
                                        </TouchableOpacity>
                                    </View>
                            ) : (
                                    <GradientButton
                                        text="🔔 Watch & Notify Me"
                                        onPress={(e) => { 
                                            e?.stopPropagation?.(); 
                                            if (onWatchFixture) {
                                                onWatchFixture(option);
                                            }
                                        }}
                                        style={{ minWidth: 200 }}
                                        gradientColors={["rgba(255, 193, 7, 0.3)", "rgba(255, 193, 7, 0.2)"]}
                                    />
                            )}
                        </View>
                    )}
                    </View>
                    
                    {/* RIGHT COLUMN: All Sportsbooks (spans full height, no scrolling needed) */}
                    <View style={styles.rightColumnFullHeight}>
                        {/* Sportsbook list - show if we have bookOdds OR availableBooks (don't require logos) */}
                        {(workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) || (workingOption?.availableBooks?.length > 0) ? (
                          <>
                          <View 
                            style={{ 
                              flex: 1, 
                              borderLeftWidth: 2,
                              borderLeftColor: 'rgba(162, 89, 255, 0.3)',
                              paddingLeft: 8,
                              marginLeft: 4,
                            }} 
                          >
                            {/* Sort toggle + book count header */}
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 4 }}>
                              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: '500' }}>
                                {workingOption.availableBooks?.length || Object.keys(workingOption.bookOdds || {}).length} {(workingOption.availableBooks?.length || Object.keys(workingOption.bookOdds || {}).length) === 1 ? 'book' : 'books'}
                              </Text>
                              <View style={{ flexDirection: 'row', gap: 4 }}>
                                <TouchableOpacity
                                  onPress={(e) => {
                                    e?.stopPropagation?.();
                                    setSortBooksBy('ev');
                                  }}
                                  style={{
                                    paddingVertical: 3,
                                    paddingHorizontal: 8,
                                    borderRadius: 6,
                                    backgroundColor: sortBooksBy === 'ev' ? 'rgba(74, 222, 128, 0.25)' : 'rgba(255,255,255,0.08)',
                                    borderWidth: 1,
                                    borderColor: sortBooksBy === 'ev' ? 'rgba(74, 222, 128, 0.5)' : 'rgba(255,255,255,0.1)',
                                  }}
                                >
                                  <Text style={{ 
                                    color: sortBooksBy === 'ev' ? '#4ade80' : 'rgba(255,255,255,0.5)', 
                                    fontSize: 9, 
                                    fontWeight: sortBooksBy === 'ev' ? '700' : '500' 
                                  }}>
                                    📊 EV%
                                  </Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                  onPress={(e) => {
                                    e?.stopPropagation?.();
                                    setSortBooksBy('odds');
                                  }}
                                  style={{
                                    paddingVertical: 3,
                                    paddingHorizontal: 8,
                                    borderRadius: 6,
                                    backgroundColor: sortBooksBy === 'odds' ? 'rgba(162, 89, 255, 0.25)' : 'rgba(255,255,255,0.08)',
                                    borderWidth: 1,
                                    borderColor: sortBooksBy === 'odds' ? 'rgba(162, 89, 255, 0.5)' : 'rgba(255,255,255,0.1)',
                                  }}
                                >
                                  <Text style={{ 
                                    color: sortBooksBy === 'odds' ? '#A259FF' : 'rgba(255,255,255,0.5)', 
                                    fontSize: 9, 
                                    fontWeight: sortBooksBy === 'odds' ? '700' : '500' 
                                  }}>
                                    💰 Odds
                                  </Text>
                                </TouchableOpacity>
                              </View>
                            </View>
                            {/* "Best" option */}
                            <TouchableOpacity 
                              onPress={(e) => {
                                e?.stopPropagation?.();
                                setSelectedBook('best');
                              }}
                              style={{ 
                                flexDirection: 'row', 
                                alignItems: 'center', 
                                gap: 6,
                                paddingVertical: 4,
                                paddingHorizontal: 8,
                                borderRadius: 8,
                                backgroundColor: selectedBook === 'best' ? 'rgba(162, 89, 255, 0.25)' : 'transparent',
                                borderWidth: selectedBook === 'best' ? 2 : 0,
                                borderColor: 'rgba(162, 89, 255, 0.6)',
                              }}
                            >
                              <View style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: sortBooksBy === 'ev' ? 'rgba(74, 222, 128, 0.3)' : 'rgba(162, 89, 255, 0.3)', justifyContent: 'center', alignItems: 'center' }}>
                                <Text style={{ fontSize: 10 }}>{sortBooksBy === 'ev' ? '📊' : '💰'}</Text>
                            </View>
                              {sortBooksBy === 'ev' && bestEvData.ev !== null ? (
                                <>
                                  <Text style={{ color: '#4ade80', fontSize: 12, fontWeight: '700', minWidth: 44 }}>
                                    {bestEvData.odds !== null ? (bestEvData.odds > 0 ? `+${bestEvData.odds}` : `${bestEvData.odds}`) : 'N/A'}
                                  </Text>
                                  <Text style={{ 
                                    color: bestEvData.ev >= 0 ? '#4ade80' : '#ef4444', 
                                    fontSize: 10, 
                                    fontWeight: '700',
                                    backgroundColor: bestEvData.ev >= 0 ? 'rgba(74, 222, 128, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                    paddingHorizontal: 4,
                                    paddingVertical: 1,
                                    borderRadius: 4,
                                  }}>
                                    {bestEvData.ev >= 0 ? '+' : ''}{bestEvData.ev.toFixed(1)}%
                                  </Text>
                                  <Text style={{ color: selectedBook === 'best' ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)', fontSize: 10, fontWeight: selectedBook === 'best' ? '700' : '500' }}>
                                    Best EV
                                  </Text>
                                </>
                              ) : (
                                <>
                                  <Text style={{ color: '#4ade80', fontSize: 12, fontWeight: '700', minWidth: 44 }}>
                                    {bestPrice !== null ? (bestPrice > 0 ? `+${bestPrice}` : `${bestPrice}`) : 'N/A'}
                                  </Text>
                                  <Text style={{ color: selectedBook === 'best' ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)', fontSize: 10, fontWeight: selectedBook === 'best' ? '700' : '500' }}>
                                    Best Odds
                                  </Text>
                                </>
                              )}
                            </TouchableOpacity>
                            
                            {(() => {
                              // Get books list - prefer availableBooks, fallback to bookOdds keys
                              const booksList = workingOption.availableBooks?.length > 0 
                                ? workingOption.availableBooks 
                                : Object.keys(workingOption.bookOdds || {});
                              
                              // Sort sportsbooks based on user preference (EV% or Odds)
                              const sortedBooks = [...booksList].sort((a, b) => {
                                const bookDataA = workingOption.bookOdds?.[a];
                                const bookDataB = workingOption.bookOdds?.[b];
                                const oddsA = extractPrice(bookDataA);
                                const oddsB = extractPrice(bookDataB);
                                
                                if (sortBooksBy === 'ev') {
                                  // Sort by EV% (highest first)
                                  const evA = typeof bookDataA === 'object' ? (bookDataA.ev_percent ?? null) : null;
                                  const evB = typeof bookDataB === 'object' ? (bookDataB.ev_percent ?? null) : null;
                                  
                                  if (evA === null && evB === null) {
                                    // Fallback to odds if no EV data
                                    if (oddsA === null || oddsA === undefined) return 1;
                                    if (oddsB === null || oddsB === undefined) return -1;
                                    return oddsB - oddsA;
                                  }
                                  if (evA === null) return 1;
                                  if (evB === null) return -1;
                                  return evB - evA; // Higher EV first
                                } else {
                                  // Sort by odds (best odds first - higher is better for bettor)
                                  if (oddsA === null || oddsA === undefined) return 1;
                                  if (oddsB === null || oddsB === undefined) return -1;
                                  return oddsB - oddsA;
                                }
                              });
                              
                              // Show all books (removed toggle - always show all)
                              return sortedBooks.map((bookName, idx) => {
                                // ⭐ FIX: Look up logo by book name directly, not by index
                                const logo = getSportsbookLogo(bookName);
                                const bookData = workingOption.bookOdds?.[bookName];
                                const odds = extractPrice(bookData);
                                const formattedOdds = odds ? (odds > 0 ? `+${odds}` : `${odds}`) : 'N/A';
                                const evPercent = typeof bookData === 'object' ? bookData.ev_percent : null;
                                const hasEv = evPercent !== null && evPercent !== undefined;
                                const isSelectedBook = selectedBook === bookName;
                                
                                return (
                                  <TouchableOpacity 
                                    key={bookName}
                                      onPress={(e) => {
                                        e?.stopPropagation?.();
                                      setSelectedBook(bookName);
                                    }}
                                    style={{ 
                                      flexDirection: 'row', 
                                      alignItems: 'center', 
                                      gap: 6,
                                      paddingVertical: 4,
                                      paddingHorizontal: 8,
                                      borderRadius: 8,
                                      backgroundColor: isSelectedBook ? 'rgba(162, 89, 255, 0.25)' : 'transparent',
                                      borderWidth: isSelectedBook ? 2 : 0,
                                      borderColor: 'rgba(162, 89, 255, 0.6)',
                                    }}
                                  >
                                    {logo ? (
                                      <Image source={{ uri: logo }} style={{ width: 18, height: 18, borderRadius: 4 }} resizeMode="contain" />
                                    ) : (
                                      <View style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center' }}>
                                        <Text style={{ fontSize: 8, color: 'rgba(255,255,255,0.6)' }}>📖</Text>
                                  </View>
                                    )}
                                    <Text style={{ color: '#4ade80', fontSize: 12, fontWeight: '700', minWidth: 44 }}>{formattedOdds}</Text>
                                    {hasEv && (
                                      <Text style={{ 
                                        color: evPercent >= 0 ? '#4ade80' : '#ef4444', 
                                        fontSize: 10, 
                                        fontWeight: '600',
                                        minWidth: 38,
                                        backgroundColor: evPercent >= 0 ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                        paddingHorizontal: 4,
                                        paddingVertical: 1,
                                        borderRadius: 4,
                                      }}>
                                        {evPercent >= 0 ? '+' : ''}{evPercent.toFixed(1)}%
                                      </Text>
                                    )}
                                    <Text style={{ color: isSelectedBook ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)', fontSize: 10, fontWeight: isSelectedBook ? '700' : '500', flex: 1 }}>
                                      {bookName}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              });
                            })()}
                          </View>
                          </>
                        ) : (sportsbookLogoUrl || logoUrl) ? (
                          <Image source={{ uri: sportsbookLogoUrl || logoUrl }} style={{ width: 40, height: 40, borderRadius: 8, opacity: 0.95 }} resizeMode="contain" />
                        ) : !homeLogoUrl && !teamLogoUrl ? (
                          <Text style={[styles.betCardAmount, { opacity: 1 }]}>{option.amount}</Text>
                            ) : null}
                        </View>
                        </View>
                </View>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    betCard: {
        position: 'relative',
        borderWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
        alignSelf: 'stretch',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        opacity: 1,
        borderRadius: 20,
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflow: 'hidden',
    },
    betCardBlur: {
        flex: 1,
        width: '100%',
        height: '100%',
        borderRadius: 20,
        padding: 14,
        justifyContent: 'center',
        alignItems: 'center',
    },
    selectedBetCard: {
        borderTopColor: 'rgba(255,255,255,0.5)',
        borderLeftColor: 'rgba(255,255,255,0.5)',
        borderRightColor: 'rgba(255,255,255,0.3)',
        borderBottomColor: 'rgba(255,255,255,0.3)',
    },
    betCardContent: {
        flex: 1,
        alignSelf: 'stretch',
        gap: 16,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: 20,
        opacity: 1,
    },
    // ⭐ TWO-COLUMN LAYOUT: Left (2 rows), Right (full height sportsbooks)
    twoColumnLayout: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        gap: 16,
    },
    leftColumn: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 12,
        minWidth: 200,
    },
    rightColumnFullHeight: {
        flexShrink: 0,
        width: 220,
        maxWidth: 240,
        alignSelf: 'stretch', // ⭐ Spans full height of card
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.1)',
        paddingLeft: 12,
        // No max height - shows all books without scrolling
    },
    rightColumn: {
        flexShrink: 0,
        maxWidth: 280,
        minWidth: 180,
        alignItems: 'flex-start',
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.1)',
        paddingLeft: 16,
    },
    infoSection: {
        flexDirection: 'column',
        gap: 6,
    },
    mathSectionInline: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 8,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.08)',
    },
    betCardActionsInline: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.08)',
    },
    betCardHeader: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
    },
    betCardInfo: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
        flexDirection: 'row',
    },
    betCardTitle: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 6,
    },
    // ⭐ LARGER title text
    betCardTitleTextLarge: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.95)',
        fontSize: 18,
        fontFamily: 'Slate',
        fontWeight: '600',
        lineHeight: 24,
        opacity: 1,
    },
    // ⭐ LARGER description text
    betCardDescriptionLarge: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: 16,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 22,
        opacity: 1,
    },
    betCardTitleText: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
        opacity: 1,
    },
    betCardDescription: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
        opacity: 1,
    },
    betCardAmount: {
        color: '#707070',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
        opacity: 1,
    },
    betCardActions: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        gap: 8,
        flexDirection: 'row',
    },
    betCardButton: {
        flex: 1,
        marginRight: 8,
    },
    booksSection: {
        alignSelf: 'stretch',
        marginTop: 6,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.06)',
        paddingTop: 6
    },
    booksLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 12, opacity: 1 },
    booksValue: { color: '#ffffff', fontSize: 13, fontWeight: '600', opacity: 1 },
    mathSection: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 8,
        marginTop: 6,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.08)'
    },
    mathHeaderRow: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    mathHeader: {
        color: '#ffffff',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '700',
        opacity: 0.95,
    },
    mathGrid: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
    },
    stakeControls: {
        flexDirection: 'column',
        gap: 6,
        alignItems: 'flex-end'
    },
    stakeQuickRow: {
        flexDirection: 'row',
        gap: 6
    },
    stakeChip: {
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)'
    },
    stakeChipActive: {
        backgroundColor: 'rgba(255,255,255,0.18)',
        borderColor: 'rgba(255,255,255,0.35)'
    },
    stakeChipText: {
        color: 'rgba(255,255,255,0.85)',
        fontWeight: '600',
        opacity: 1
    },
    stakeChipTextActive: {
        color: '#000',
        opacity: 1
    },
    stepperRow: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center'
    },
    stepperBtn: {
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)'
    },
    stepperText: {
        color: '#ffffff',
        fontWeight: '700',
        opacity: 1
    },
    stakeValue: {
        color: '#ffffff',
        fontWeight: '700',
        opacity: 1
    },
    stakeInputInline: {
        marginTop: 2,
        color: '#ffffff',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)',
        borderRadius: 6,
        paddingVertical: 4,
        paddingHorizontal: 8,
        minWidth: 90,
        opacity: 1
    },
    metricsRow: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 20,
        marginTop: 6
    },
    metricCol: {
        flex: 1,
        minWidth: 110,
        gap: 4
    },
    metricColFirst: {
        paddingLeft: 0
    },
    metricColSep: {
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.06)',
        paddingLeft: 12
    },
    metricLabel: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 12,
        opacity: 1
    },
    metricValue: {
        color: '#ffffff',
        fontSize: 16,
        lineHeight: 20,
        fontWeight: '700',
        opacity: 1
    },
    metricSubtext: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 11,
        opacity: 1
    },
    bookDetailsBtn: {
        marginTop: 4,
        paddingVertical: 2,
        paddingHorizontal: 6,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 4,
        alignSelf: 'flex-start'
    },
    bookDetailsBtnText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 10,
        opacity: 1
    },
    bookDetailsSection: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.1)',
        alignSelf: 'stretch'
    },
    bookDetailsTitle: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 12,
        fontWeight: '600',
        marginBottom: 8,
        opacity: 1
    },
    bookDetailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 4,
        opacity: 1
    },
    bookDetailName: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 11,
        opacity: 1
    },
    bookDetailOdds: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 11,
        fontWeight: '500',
        opacity: 1
    },
    evPositive: { color: '#9AFF9A', opacity: 1 },
    evNegative: { color: '#FF9A9A', opacity: 1 },
    mathRow: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 48,
    },
    mathItem: {
        flex: 1,
        height: 40,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 8,
    },
    mathItemContent: {
        alignSelf: 'stretch',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 8,
    },
    mathLabelValue: {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
        flexDirection: 'row',
    },
    mathLabel: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '600',
        lineHeight: 18,
        opacity: 1,
    },
    mathValue: {
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 18,
        opacity: 1,
    },
    mathValueWhite: {
        color: '#ffffff',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '600',
        lineHeight: 18,
        opacity: 1,
    },
    mathInput: {
        color: '#ffffff',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)',
        borderRadius: 6,
        opacity: 1,
        paddingVertical: 6,
        paddingHorizontal: 8,
        minWidth: 80,
    },
    showAllBooksBtn: {
        marginTop: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.3)',
        alignItems: 'center',
    },
    showAllBooksBtnText: {
        color: '#60A5FA',
        fontSize: 11,
        fontWeight: '600',
    },
});

export default LegBetCard;
